// src/features/Learner/LessonView.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import './LessonView.css';
import UnitViewer from './UnitViewer';

const LessonView = () => {
  const { lessonId } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLessonData = async () => {
      if (!currentUser || !lessonId) return;
      try {
        setLoading(true);

        const lessonDoc = await getDoc(doc(db, 'lessons', lessonId));
        if (!lessonDoc.exists()) {
          setError('Lesson not found.');
          setLoading(false);
          return;
        }

        const lessonData = { id: lessonDoc.id, ...lessonDoc.data() };
        setLesson(lessonData);

        const catalogId = lessonData.catalogId;
        if (catalogId) {
          const [catalogDoc, userCatalogsSnapshot] = await Promise.all([
            getDoc(doc(db, 'catalogs', catalogId)),
            getDocs(query(
              collection(db, 'userCatalogs'),
              where('userId', '==', currentUser.uid),
              where('catalogId', '==', catalogId)
            ))
          ]);

          if (userCatalogsSnapshot.empty) {
            setError('You do not have access to this lesson.');
            setLoading(false);
            return;
          }

          if (catalogDoc.exists()) {
            setCatalog({ id: catalogDoc.id, ...catalogDoc.data() });
          }

          // Mark this lesson as visited in the learnerProgress summary.
          // visitedLessonIds is read by LearnerDashboard to count visited
          // lessons per catalog without fetching packages or units.
          // Fire-and-forget — UI doesn't depend on this completing.
          markLessonVisited(currentUser.uid, lessonId);
        }
      } catch (err) {
        console.error('Error fetching lesson data:', err);
        setError('An error occurred while loading the lesson.');
      } finally {
        setLoading(false);
      }
    };

    fetchLessonData();
  }, [currentUser, lessonId]);

  const handleBackToCatalog = () => {
    if (catalog) {
      navigate(`/learn/catalog/${catalog.id}`);
    } else {
      navigate('/learn/dashboard');
    }
  };

  if (loading) return <div className="loading">Loading lesson...</div>;

  if (error) {
    return (
      <div className="error-container">
        <p className="error-message">{error}</p>
        <button className="back-button" onClick={() => navigate('/learn/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div className="error-container">
        <p className="error-message">Lesson not found.</p>
        <button className="back-button" onClick={() => navigate('/learn/dashboard')}>
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="lesson-view">

      {/* Compact header */}
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 12px',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #e5e7eb',
        flexShrink: 0
      }}>
        <button
          onClick={handleBackToCatalog}
          aria-label="Back to lessons"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '36px',
            height: '36px',
            borderRadius: '50%',
            border: 'none',
            backgroundColor: '#e8a327',
            color: '#ffffff',
            fontSize: '16px',
            cursor: 'pointer',
            flexShrink: 0,
            WebkitTapHighlightColor: 'transparent'
          }}
        >
          ◀
        </button>

        <h1 style={{
          fontSize: '1rem',
          fontWeight: 700,
          color: '#111827',
          margin: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {lesson.title}
        </h1>
      </div>

      {/* ---------------------------------------------------------------------------
          Pass catalogId down so UnitViewer can forward it to markPackageVisited.
          This is the only change from the original — catalog?.id is already
          available in state by the time UnitViewer mounts.
      --------------------------------------------------------------------------- */}
      <UnitViewer lessonId={lessonId} catalogId={catalog?.id} />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Write lessonId to visitedLessonIds on the learnerProgress doc.
// Called once when the lesson loads. Skips if already recorded.
// ---------------------------------------------------------------------------
async function markLessonVisited(uid, lessonId) {
  try {
    const progressRef = doc(db, 'learnerProgress', uid);
    const snap = await getDoc(progressRef);

    if (snap.exists()) {
      const existing = snap.data().visitedLessonIds || [];
      if (existing.includes(lessonId)) return; // already recorded
      await updateDoc(progressRef, {
        visitedLessonIds: [...existing, lessonId]
      });
    } else {
      await setDoc(progressRef, {
        learnerId: uid,
        visitedLessonIds: [lessonId],
        visitedPackages: {},
        visitedUnits: [],
        visitedPages: [],
        completedUnits: {},
        lastVisited: new Date(),
        lastUpdated: new Date()
      });
    }
  } catch (err) {
    // Non-critical — dashboard falls back to 0 if missing
    console.warn('markLessonVisited failed:', err);
  }
}

export default LessonView;